import React, { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import {
	Box,
	Button,
	TextField,
	IconButton,
	Stack,
	InputAdornment,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Search } from '@mui/icons-material';
import AppAutocomplete from 'src/globalComponents/AppAutoComplete';

export interface FilterOption {
	value: string;
	label: string;
}

export interface FilterConfig {
	field: string;
	label: string;
	extractUnique: (data: any[]) => string[];
}

interface BaseGridToolbarProps {
	// Search props
	filter: string;
	setFilter: Dispatch<SetStateAction<string>>;

	// Dynamic filter props
	filters: Record<string, string>;
	setFilters: (filters: Record<string, string>) => void;

	// Configuration
	filterConfigs: FilterConfig[];
	allData: any[];
	handleRefresh?: () => void;

	// Optional add button
	onAdd?: () => void;
	addButtonLabel?: string;
}

const BaseGridToolbar: React.FC<BaseGridToolbarProps> = ({
	filter,
	setFilter,
	filters,
	setFilters,
	filterConfigs,
	allData,
	handleRefresh,
	onAdd,
	addButtonLabel = 'Add New',
}) => {
	// Responsive input management
	const [localSearchInput, setLocalSearchInput] = React.useState(filter);
	const debounceTimer = useRef<NodeJS.Timeout>();
	const inputRef = useRef<HTMLInputElement>(null);
	const [isFocused, setIsFocused] = React.useState(false);
	const [cursorPosition, setCursorPosition] = React.useState(0);

	// Sync local input with parent filter
	useEffect(() => {
		setLocalSearchInput(filter);
	}, [filter]);

	// Debounced filtering
	useEffect(() => {
		if (debounceTimer.current) {
			clearTimeout(debounceTimer.current);
		}

		debounceTimer.current = setTimeout(() => {
			if (localSearchInput !== filter) {
				setFilter(localSearchInput);
			}
		}, 1);

		return () => {
			if (debounceTimer.current) {
				clearTimeout(debounceTimer.current);
			}
		};
	}, [localSearchInput, filter, setFilter]);

	// Focus management
	useEffect(() => {
		if (
			isFocused &&
			inputRef.current &&
			document.activeElement !== inputRef.current
		) {
			inputRef.current.focus();
			inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
		}
	});

	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.focus();
			setIsFocused(true);
		}
	}, []);

	// Generate filter options dynamically
	const generateFilterOptions = (config: FilterConfig): FilterOption[] => {
		if (!allData || allData.length === 0) return [];

		const uniqueValues = [
			...new Set(
				config
					.extractUnique(allData)
					.filter((value) => value && value.toString().trim())
			),
		].sort((a, b) => a.toString().localeCompare(b.toString()));

		return [
			{ value: '', label: 'All' },
			...uniqueValues.map((value) => ({
				value: value.toString(),
				label:
					value.toString().charAt(0).toUpperCase() + value.toString().slice(1),
			})),
		];
	};

	return (
		<Box sx={{ paddingTop: 1, paddingBottom: 2 }}>
			<Stack direction="row" spacing={2} alignItems="center">
				{/* Search Input */}
				<TextField
					inputRef={inputRef}
					sx={{
						width: {
							xs: '100px',
							sm: '180px',
							md: '220px',
							lg: '260px',
						},
					}}
					aria-label="Search"
					placeholder="Search"
					size="small"
					variant="outlined"
					value={localSearchInput}
					onChange={(e) => {
						setLocalSearchInput(e.target.value);
						setCursorPosition(e.target.selectionStart || 0);
					}}
					onFocus={() => setIsFocused(true)}
					onBlur={() => setIsFocused(false)}
					onSelect={(e) =>
						setCursorPosition(e.currentTarget.selectionStart || 0)
					}
					slotProps={{
						input: {
							startAdornment: (
								<InputAdornment position="start">
									<Search fontSize="small" />
								</InputAdornment>
							),
						},
					}}
				/>

				{/* Dynamic Filter Dropdowns */}
				{filterConfigs.map((config) => {
					const options = generateFilterOptions(config);
					return (
						<AppAutocomplete
							key={config.field}
							options={options}
							getOptionLabel={(option) => option.label}
							value={
								options.find((opt) => opt.value === filters[config.field]) ||
								null
							}
							onChange={(_, newValue) => {
								setFilters({
									...filters,
									[config.field]: newValue?.value || '',
								});
							}}
							sx={{
								width: {
									xs: '100px',
									sm: '140px',
									md: '180px',
									lg: '220px',
								},
							}}
							renderInput={(params) => (
								<TextField {...params} label={config.label} size="small" />
							)}
							isOptionEqualToValue={(option, value) =>
								option.value === value.value
							}
						/>
					);
				})}

				{/* Spacer to push refresh button to the right */}
				<Box sx={{ flexGrow: 1 }} />

				{/* Optional Add Button */}
				{onAdd && (
					<Button
						variant="contained"
						color="primary"
						onClick={onAdd}
						sx={{
							minWidth: {
								xs: '90px',
								sm: '120px',
								md: '140px',
							},
						}}
					>
						{addButtonLabel}
					</Button>
				)}

				{/* Refresh Button - always last */}
				<IconButton
					color="primary"
					onClick={handleRefresh}
					sx={{
						ml: onAdd ? 1 : 0,
						border: '1px dotted',
						borderColor: 'primary.main',
					}}
				>
					<RefreshIcon />
				</IconButton>
			</Stack>
		</Box>
	);
};

export default React.memo(BaseGridToolbar);
